import { Menu, MenuItem, PopoverOrigin, useMediaQuery } from '@mui/material';
import React, { CSSProperties } from 'react';
import { bluePlanetTheme } from 'ui/theme';
import type { IconProps } from 'ui/elements/icons/SvgIcon';
import IconButton from 'ui/elements/icons/IconButton';
import EllipsisVertical from 'ui/elements/icons/EllipsisVertical';
import { IconColor } from 'ui/elements/icons/SvgIcon';
import Tooltip from 'ui/elements/Tooltip';
import DrawerBottom, { DrawerItem } from 'ui/elements/DrawerBottom';
import { contentSpacing, halfSpacing, quarterSpacing } from 'ui/theme/themeConstants';
import styled from '@emotion/styled';
import NestedMenuItem from './NestedMenuItem';

function applyIconDefaults(icon: JSX.Element): JSX.Element {
  return React.cloneElement(icon, { fontSize: 'tiny', isFaded: true, ...icon.props } as Partial<IconProps>);
}

export type PopMenuItem =
  | {
      onClick?: () => void;
      icon?: JSX.Element;
      text: JSX.Element | string;
      selected?: boolean;
      onMouseEnter?: () => void;
      onMouseLeave?: () => void;
      backgroundColor?: string;
      spacing?: 'default' | 'compact';
      subMenu?: PopMenuItem[];
    }
  | 'divider';

interface Props {
  style?: CSSProperties;
  className?: string;
  items?: PopMenuItem[];
  icon?: JSX.Element;
  tooltip?: string;
  iconColor?: IconColor;
  staticItems?: React.ReactNode;
  renderAnchor?: (onClick: (e: React.MouseEvent<HTMLElement>) => void, isOpen: boolean) => JSX.Element;
  onOpen?: () => void;
  onClose?: () => void;
  anchorOrigin?: PopoverOrigin;
}

const Divider = styled.hr`
  margin: ${quarterSpacing} ${contentSpacing};
`;

export default function PopMenu({
  style,
  className,
  icon,
  tooltip,
  iconColor,
  renderAnchor,
  staticItems,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'center',
  },
  items = [],
  onOpen,
  onClose,
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  function handleClose() {
    setAnchorEl(null);
    onClose?.();
  }

  function handleClick(e: React.MouseEvent<HTMLElement>) {
    setAnchorEl(anchorEl ? null : e.currentTarget);
    if (!anchorEl) onOpen?.();
  }

  const isMobile = useMediaQuery(bluePlanetTheme.breakpoints.down('sm'));

  return (
    <div className={className}>
      {renderAnchor ? (
        renderAnchor(handleClick, isOpen)
      ) : tooltip ? (
        <Tooltip title={tooltip}>
          <span>
            <IconButton color={iconColor ?? 'indigo'} onClick={handleClick}>
              {icon ?? <EllipsisVertical />}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <IconButton color={iconColor ?? 'indigo'} onClick={handleClick}>
          {icon ?? <EllipsisVertical />}
        </IconButton>
      )}

      {isMobile && (
        <DrawerBottom isOpen={isOpen} onClose={handleClose}>
          {items.map((item, i) =>
            item === 'divider' ? (
              <Divider key={i} />
            ) : (
              <DrawerItem
                itemSpacing={item.spacing}
                backgroundColor={item.backgroundColor}
                key={i}
                prefix={item.icon ? applyIconDefaults(item.icon) : undefined}
                onClick={
                  item.onClick
                    ? () => {
                        item.onClick?.();
                        handleClose();
                      }
                    : undefined
                }
              >
                {item.text}
              </DrawerItem>
            ),
          )}
        </DrawerBottom>
      )}
      {!isMobile && (
        <Menu
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={anchorOrigin}
          transformOrigin={{ horizontal: anchorOrigin?.horizontal, vertical: 'top' }}
          slotProps={{ paper: { style } }}
        >
          {items.map((item, i) =>
            item === 'divider' ? (
              <Divider key={i} />
            ) : item.subMenu ? (
              <NestedMenuItem
                key={i}
                parentMenuOpen={isOpen}
                subMenu={item.subMenu.map((subItem, j) => {
                  if (subItem === 'divider') {
                    return <Divider key={`submenu-divider-${i}-${j}`} />;
                  }
                  if (subItem.subMenu) {
                    return (
                      <NestedMenuItem
                        key={`submenu-${i}-${j}`}
                        parentMenuOpen={isOpen}
                        subMenu={subItem.subMenu.map((subSubItem, k) => {
                          if (subSubItem === 'divider') {
                            return <Divider key={`subsubmenu-divider-${i}-${j}-${k}`} />;
                          }
                          return (
                            <MenuItem
                              key={`subsubmenu-${i}-${j}-${k}`}
                              sx={{
                                padding:
                                  subSubItem.spacing === 'compact'
                                    ? `${quarterSpacing} ${contentSpacing}`
                                    : undefined,
                              }}
                              onClick={() => {
                                subSubItem.onClick?.();
                                handleClose();
                              }}
                            >
                              {subSubItem.text}
                            </MenuItem>
                          );
                        })}
                      >
                        {subItem.icon && (
                          <span className="u-flex u-half-spacing-right">{applyIconDefaults(subItem.icon)}</span>
                        )}
                        {subItem.text}
                      </NestedMenuItem>
                    );
                  }
                  return (
                    <MenuItem
                      key={`submenu-${i}-${j}`}
                      selected={subItem.selected}
                      onClick={() => {
                        subItem.onClick?.();
                        handleClose();
                      }}
                    >
                      {subItem.text}
                    </MenuItem>
                  );
                })}
              >
                {item.icon && <span className="u-flex u-half-spacing-right">{applyIconDefaults(item.icon)}</span>}
                {item.text}
              </NestedMenuItem>
            ) : (
              <MenuItem
                key={i}
                data-open={!!open || undefined}
                className={className}
                sx={{
                  backgroundColor: item.backgroundColor,
                  padding: item.spacing === 'compact' ? `${quarterSpacing} ${contentSpacing}` : undefined,
                  cursor: item.onClick ? 'pointer' : 'default',
                  '&:hover': item.onClick ? undefined : { backgroundColor: 'transparent' },
                  '&.Mui-selected': {
                    backgroundColor: bluePlanetTheme.bluePlanetPalette.blue.veryLight,
                    fontWeight: bluePlanetTheme.typography.fontWeightRegular,
                    '&:hover': {
                      backgroundColor: bluePlanetTheme.bluePlanetPalette.blue.veryLight,
                    },
                  },
                }}
                onMouseEnter={item.onMouseEnter}
                onMouseLeave={item.onMouseLeave}
                selected={item.selected}
                onClick={
                  item.onClick
                    ? () => {
                        item.onClick?.();
                        handleClose();
                      }
                    : undefined
                }
              >
                {item.icon && <span className="u-flex u-half-spacing-right">{applyIconDefaults(item.icon)}</span>}
                {item.text}
              </MenuItem>
            ),
          )}
          {staticItems && <div style={{ padding: `${halfSpacing} ${contentSpacing}` }}>{staticItems}</div>}
        </Menu>
      )}
    </div>
  );
}
